/**
 * @file  src\pages\AgentWantAdConfirmation.js
 * @brief  This file is responsible for agent want ad confirmation section
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import _ from "lodash";
import ConfirmMap from "../images/confirm-map.png";
import LakeViewIcon from "../images/lake-view-icon.svg";
import ConsumerIcon from "../images/consumer-icon.svg";
import {
  getUserId,
  setLocalStorage,
  loadTimeLineHtml,
  loadPreferredLocationHtml,
  clearLocalStorageOnLogout,
  locationDisplay,
} from "../helpers/v2/common";
import { getNiceToHaves } from "../services/commonServices";
import {
  BUY,
  LONG_RENT,
  SHORT_RENT,
  LOT_LAND,
  BUY_LAND,
  AGENT_WANTAD_KEY,
  IS_LAND,
  WANT_AD_KEY,
  IS_MULTI_FAMILY,
  NICE_2_HAVES_CATEGORIES_MASTER_LIST,
  NICE_2_HAVES_LOT_LAND_CATEGORIES,
} from "../constants/common";
import { useNavigate } from "react-router-dom";
import {
  saveMemberWantAd,
  saveMemberWantAdstrengthenData,
} from "../services/v2/wantAdService";
import { pageURLs } from "@/constants";
import { convertToNumber } from "../helpers/validations";
import { getRequestedConcessions } from "../helpers/concessions";
import FinancingIcon from "../images/financing-icon.svg";
import PrResIcon from "../images/primary-residence-icon.svg";
import { buildWantAdMustHavesHTML } from "../helpers/v2/createWantAd";
import Tooltip from "react-bootstrap/Tooltip";
import {
  getCombinedText,
  getPropertyIcon,
} from "../helpers/getPropertyIcons";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { userLogout } from "../reducers/login";
import { PageWrapper } from "@/components/PageWrapper";
import PlaceHolderIcon from "../images/dpdown-placeholder-icon.svg";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { handleMapImageUpload } from "@/helpers/inventory";
import useLoading from "@/hooks/useLoading";
import MapboxComponent from "../components/Common/MapboxComponent";

const LOADING_KEY = "saveWantAd";

const AgentWantAdConfirmation = (props) => {

  const navigate = useNavigate();

  const { mls, isGitchaDefaultWebsite } = useCurrentWebsite();
  const { startLoading, stopLoading } = useLoading();

  const formValues = JSON.parse(localStorage.getItem(AGENT_WANTAD_KEY)); // to set the want-ad data
  const [niceToHavesList, setNiceToHavesList] = useState([]);
  const adType = _.get(formValues, "adType", "");
  const propertyType = convertToNumber(_.get(formValues, "prprtyType", ""));
  const mapImage = _.get(formValues, "mapImg", null);
  const lat = _.get(formValues, "lat", null);
  const lng = _.get(formValues, "lng", null);
  const mapRadius = _.get(formValues, "mapRadius", null);
  const drawnPolygon = _.get(formValues, "drawnPolygon", null);

  const videoUrl = _.get(formValues, "videoUrl", null);
  const adTypeBasedOnProperty = propertyType === IS_LAND ? LOT_LAND : adType;
  const pageTopRef = useRef(null);
  const propertySubTypes = _.get(formValues, "prptySubTypes", []);
  const [isVertical, setIsVertical] = useState(false);
  const [buyerAgreementError, setBuyerAgreementError] = useState(false);
  //check if video is vertical
  useLayoutEffect(() => {
    const videoElement = document.getElementById("videoPlayer");
    if (videoElement) {
      const { videoWidth, videoHeight } = videoElement;
      setIsVertical(videoHeight > videoWidth);
    }
  });
  useEffect(() => {
    window.scrollTo(0, pageTopRef.current.offsetTop); // focus to the top section
    getNiceToHaves(
      adTypeBasedOnProperty === LOT_LAND ? BUY_LAND : adTypeBasedOnProperty,
    )
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const list = _.get(response, "result", null);
          setNiceToHavesList(list);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, []);
  /**
   * To load nice to haves
   * @param {*} section
   * @returns
   */
  const loadEachNiceToHave = (section) => {
    const nice2Have = _.get(formValues, "nice2Have", []);
    const selectedItems =
      nice2Have && nice2Have.filter((item) => item.tab === section);
    const masterArray = _.get(niceToHavesList, section, []);
    return loadNiceToHaveList(masterArray, selectedItems, section.charAt(0));
  };

  const finalNiceToHaveList = () => {
    const niceToHaveMasterArray = adTypeBasedOnProperty === LOT_LAND ?
      NICE_2_HAVES_LOT_LAND_CATEGORIES.map((item) => item.category) :
      NICE_2_HAVES_CATEGORIES_MASTER_LIST.map((item) => item.category);

    const finalList =
      niceToHaveMasterArray &&
      niceToHaveMasterArray.map((nh) => {
        return loadEachNiceToHave(nh);
      });
    return finalList;
  };

  const loadNiceToHaveList = (masterList, selectedItems, uniqueKey) => {
    return (
      selectedItems &&
      selectedItems.map((nt) => {
        const id = _.get(nt, "id");
        const masterItem =
          masterList && masterList.filter((item) => item.Id === id);
        const itemText = _.get(masterItem[0], "Text", "");
        const icon = _.get(masterItem[0], "Icon", "");
        return (
          <div className="nicetohaves-tag" key={id + uniqueKey}>
            <div className="nicetohaves-tag-icon">
              <img
                src={
                  icon
                    ? (() => {
                      try {
                        return require("../images/" + icon);
                      } catch (e) {
                        return PlaceHolderIcon;
                      }
                    })()
                    : PlaceHolderIcon
                }
                style={{ width: 16, height: 16 }}
              />
            </div>
            <span>{itemText}</span>
          </div>
        );
      })
    );
  };

  const niceToHaves = finalNiceToHaveList();
  const isItemNotExist = /^,+$/.test(niceToHaves);
  /**
   * To load ad title
   * @returns
   */
  const loadAdTitle = () => {
    let adTypeTile = "Buy";
    if (adType === LONG_RENT) {
      adTypeTile = "Rent";
    } else if (adType === SHORT_RENT) {
      adTypeTile = "Stay";
    }
    return (
      <>
        {" "}
        <span className="want-ad-tag align-middle green-tag-bg mb-0 f-size-10">
          {adTypeTile}
        </span>
      </>
    );
  };
  /**
   * To load stregthen want ad details
   * @returns
   */
  const loadStrengthenDetails = () => {
    if (adType === BUY) {
      return (
        <>
          {" "}
          {(_.get(formValues, "prchTypeTxt", "") ||
            _.get(formValues, "purchaseText", "")) && (
              <li>
                <span className="list-icn-wrap">
                  <img src={PrResIcon} alt="" />
                </span>
                {_.get(formValues, "purchaseText", "") ||
                  _.get(formValues, "prchTypeTxt", "")}
                {(_.get(formValues, "is1031Exchange", "") == "Yes" ||
                  _.get(formValues, "is1031Exchange", "") == 1) &&
                  " - 1031 exchange"}
              </li>
            )}
          {(_.get(formValues, "finTypeTxt", "") ||
            _.get(formValues, "pFinanceText", "")) && (
              <li>
                <span className="list-icn-wrap">
                  <img src={FinancingIcon} alt="" />
                </span>
                Financing:{" "}
                {_.get(formValues, "finTypeTxt", "") ||
                  _.get(formValues, "pFinanceText", "")}
                {(_.get(formValues, "isPreApprLoan", "") == "Yes" ||
                  _.get(formValues, "isPreApprLoan", "") == 1) &&
                  " - pre approved"}
              </li>
            )}
        </>
      );
    } else if (adType === LONG_RENT) {
      return (
        <>
          {" "}
          {_.get(formValues, "creditScoreText", "") && (
            <li>
              <span className="list-icn-wrap">
                <img src={FinancingIcon} alt="" />
              </span>
              Credit Score : {_.get(formValues, "creditScoreText", "")}
            </li>
          )}
        </>
      );
    }
  };
  /**
   * To load timeline
   * @returns
   */
  const loadTimeLine = () => {
    return loadTimeLineHtml(adType, adTypeBasedOnProperty, formValues);
  };
  /**
   * To load preferred location
   * @returns
   */
  const loadPreferredLocation = () => {
    return loadPreferredLocationHtml(formValues);
  };
  /**
   * To load must haves
   * @returns
   */
  const loadAdMustHaves = () => {
    return buildWantAdMustHavesHTML({
      adType: adType,
      adTypeBasedOnProperty: adTypeBasedOnProperty,
      formValues: formValues,
    });
  };

  const handleStateClear = () => {
    dispatch(userLogout());
    clearLocalStorageOnLogout();
    navigate(pageURLs.home);
  };
  /**
   * To save want ad data
   * @param {*} isPostAd
   */
  const saveMemberWantAdData = async (isPostAd) => {
    startLoading(LOADING_KEY);

    let updatedState = {
      pTypeTxt: _.get(formValues, "prptyTypeTxt", ""),
      adType: _.get(formValues, "adType", "") + "_STRENGTHEN",
    };
    let userId = getUserId();
    let requestPayLoad = formValues;
    if (!requestPayLoad) {
      requestPayLoad = JSON.parse(localStorage.getItem("agentWantAdData"));
    }

    const mapImageUrl = await handleMapImageUpload({
      lat: _.get(formValues, "lat", ""),
      lng: _.get(formValues, "lng", ""),
      mapZoom: _.get(formValues, "mapZoom"),
      s3ImageFolder: _.get(formValues, "s3ImageFolder", ""),
    });
    if (mapImageUrl) {
      requestPayLoad.mapImg = mapImageUrl;
    }

    if (userId && requestPayLoad) {
      requestPayLoad.cmpldStep = _.get(formValues, "actvStep", 1);
      if (isPostAd) {
        requestPayLoad.IsSavedDraft = false;
        requestPayLoad.IsPublished = true;
        delete requestPayLoad.tlText;
        delete requestPayLoad.prptyTypeTxt;
        delete requestPayLoad.wantAdValErrors;
      } else {
        requestPayLoad.IsSavedDraft = true;
        requestPayLoad.IsPublished = false;
      }
      let arrayValue = [];
      if (requestPayLoad?.zipIdValues) {
        requestPayLoad?.zipIdValues.map((zip) => {
          arrayValue.push(zip?.zipId);
        });
        requestPayLoad.zipId = arrayValue;
      } else if (requestPayLoad?.zipCode) {
        requestPayLoad?.zipCode.map((zip) => {
          if (zip?.zipId && zip?.zipId != null) {
            arrayValue.push(zip?.zipId);
          }
        });
        requestPayLoad.zipId = arrayValue;
      }
      requestPayLoad.isZipcodeBased = requestPayLoad.zipId?.length > 0 ? true : false;
      requestPayLoad.mls = mls;
      //IMPORTANT CHECK: SYNDICATE TO THE PUBLIC IS SET TO TRUE FOR GITCHA DEFAULT WEBSITE
      if (isGitchaDefaultWebsite) {
        requestPayLoad.syndicateToThePublic = true;
      }
      saveMemberWantAd(requestPayLoad)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (isPostAd) {
              if (
                requestPayLoad.purchaseType ||
                requestPayLoad.creditScore ||
                requestPayLoad.prchTypeId ||
                requestPayLoad.pFinance
              ) {
                saveStrengthenData(
                  _.get(responseResult, "wantAdId", "")
                    ? _.get(responseResult, "wantAdId", "")
                    : _.get(formValues, "wantAdId", ""),
                  isPostAd,
                );
              } else {
                stopLoading(LOADING_KEY);
                const slug = _.get(responseResult, "slug");
                updatedState.wantAdId = _.get(responseResult, "wantAdId", "")
                  ? _.get(responseResult, "wantAdId", "")
                  : _.get(formValues, "wantAdId", "");
                updatedState.minBdgt = _.get(formValues, "minBdgt", "");
                updatedState.maxBdgt = _.get(formValues, "maxBdgt", "");

                setLocalStorage(WANT_AD_KEY, updatedState);
                localStorage.removeItem(AGENT_WANTAD_KEY);
                navigate(`${pageURLs.wantAdDetails}/${slug}`, { state: { isFromConfirmationPage: true } });
              }
            } else {
              if (
                requestPayLoad.purchaseType ||
                requestPayLoad.creditScore ||
                requestPayLoad.prchTypeId ||
                requestPayLoad.pFinance
              ) {
                saveStrengthenData(
                  _.get(responseResult, "wantAdId", "")
                    ? _.get(responseResult, "wantAdId", "")
                    : _.get(formValues, "wantAdId", ""),
                  isPostAd,
                );
              } else {
                stopLoading(LOADING_KEY);
                localStorage.removeItem(AGENT_WANTAD_KEY);
                navigate(pageURLs.agentMyWantAdListings);
              }
            }
          } else {
            console.log("Something went wrong");
            stopLoading(LOADING_KEY);
          }
        })
        .catch((err) => {
          console.log("Error", err);
          stopLoading(LOADING_KEY);
        });
    } else {
      stopLoading(LOADING_KEY);
      handleStateClear();
    }
  };

  /**
   * To save stengthen want ad data
   * @param {*} wantAdIdValue
   * @param {*} isPostAd
   */
  const saveStrengthenData = async (wantAdIdValue, isPostAd) => {
    const wantAdId = wantAdIdValue;
    let updatedState = {
      pTypeTxt: _.get(formValues, "prptyTypeTxt", ""),
      adType: _.get(formValues, "adType", "") + "_STRENGTHEN",
    };
    let requestPayLoad = {};
    let isApproveLoan = "";
    if (
      _.get(formValues, "isPreApprLoan", null) === "Yes" ||
      _.get(formValues, "isPreApprLoan", null) == true
    ) {
      isApproveLoan = true;
    } else if (
      _.get(formValues, "isPreApprLoan", null) === "No" ||
      _.get(formValues, "isPreApprLoan", null) == true
    ) {
      isApproveLoan = false;
    }
    requestPayLoad.isPreApprLoan = isApproveLoan;
    const is1031Exchange =
      _.get(formValues, "is1031Exchange", "") == "Yes" ||
        _.get(formValues, "is1031Exchange", "") == 1
        ? true
        : false;
    requestPayLoad.wantAdId = wantAdId;
    requestPayLoad.isPreApprLoan = isApproveLoan;
    requestPayLoad.is1031Exchange = is1031Exchange;
    requestPayLoad.completedStep = 1;
    requestPayLoad.adType =
      _.get(formValues, "adType", "") == BUY
        ? "BUY_STRENGTHEN"
        : "LTR_STRENGTHEN";
    requestPayLoad.creditScore = _.get(formValues, "creditScore", "");
    requestPayLoad.maxBdgt = _.get(formValues, "maxBdgt", "");
    requestPayLoad.minBdgt = _.get(formValues, "minBdgt", "");
    requestPayLoad.pFinance =
      _.get(formValues, "pFinance", "") || _.get(formValues, "finTypeId", "");
    requestPayLoad.purchaseType =
      _.get(formValues, "purchaseType", "") ||
      _.get(formValues, "prchTypeId", "");
    await saveMemberWantAdstrengthenData(requestPayLoad)
      .then((response) => {
        stopLoading(LOADING_KEY);
        const responseResult = _.get(response, "result", null);

        if (response.errorCode === 0 && responseResult) {
          const slug = _.get(responseResult, "slug");
          updatedState.wantAdId = wantAdId;
          if (isPostAd) {
            const slug = _.get(responseResult, "slug");
            updatedState.wantAdId = wantAdId;
            updatedState.minBdgt = _.get(formValues, "minBdgt", "");
            updatedState.maxBdgt = _.get(formValues, "maxBdgt", "");

            setLocalStorage(WANT_AD_KEY, updatedState);
            localStorage.removeItem(AGENT_WANTAD_KEY);
            navigate(`${pageURLs.wantAdDetails}/${slug}`, { state: { isFromConfirmationPage: true } });
          } else {
            localStorage.removeItem(AGENT_WANTAD_KEY);
            navigate(pageURLs.agentMyWantAdListings);
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
        stopLoading(LOADING_KEY);
      });
  };

  const renderTooltip = (props) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {propertyType === IS_LAND || propertyType === IS_MULTI_FAMILY
        ? getTooltipText(propertyType)
        : propertySubTypes &&
        propertySubTypes.length > 0 &&
        getCombinedText(propertySubTypes)}
    </Tooltip>
  );

  return (
    <>
      <PageWrapper ref={pageTopRef}>
        <div className="m-0 pb-4">
          <div className="main-content p-0 agent-no-login-content">
            {/* To do when admin login is true when we use (m-2) */}
            {/* <div className="row m-2"> */}
            <div className="row m-0">
              <div className="col-md-12">
                <div className="ready-to-post-wrap mw-724">
                  <div className="ready-to-post-top d-sm-flex justify-content-between">
                    <div>
                      <h2>Ready to post?</h2>
                      {_.get(formValues, "clientName", "") ||
                        _.get(formValues, "clientData.label", "") ? (
                        <h6 className="long-txt-fix">
                          Client:{" "}
                          {_.get(formValues, "clientName", "")
                            ? _.get(formValues, "clientName", "")
                            : _.get(formValues, "clientData.label", "")}{" "}
                          <span className="long-txt-fix">
                            (Only visible to you)
                          </span>
                        </h6>
                      ) : null}
                    </div>
                    <div className="d-block mt-3 mt-sm-0">
                      {_.get(formValues, "isPublished", "") == 0 && (
                        <button
                          className="btn block-link-btn me-2"
                          onClick={() => saveMemberWantAdData(false)}
                        >
                          Save Draft
                        </button>
                      )}

                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          if (
                            adType === BUY &&
                            !_.get(formValues, "buyersAgreementSigned", false)
                          ) {
                            setBuyerAgreementError(true);
                            return;
                          }
                          saveMemberWantAdData(true);
                        }}
                      >
                        Post
                      </button>
                    </div>
                  </div>
                  {buyerAgreementError && (
                    <div className="text-danger mt-2">
                      The buyer agreement must be signed before this
                      want-listing can be posted. Go back and check the Buyer
                      Agreement Form Signed box.
                    </div>
                  )}
                  <div className="ready-to-post-bottom">
                    <div className="confirmation-top-sec d-md-flex justify-content-between">
                      <div>
                        <div className="confirm-sub-title mb-1">
                          I'm looking to:
                          {loadAdTitle()}
                        </div>
                        <h2 className="long-txt-fix">
                          {_.get(formValues, "adTitle", "")}{" "}
                        </h2>
                        <span className="trailing-txt long-txt-fix">
                          {locationDisplay(formValues)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary align-self-start"
                        onClick={() =>
                          formValues?.wantAdId
                            ? navigate(`${pageURLs.editWantAdForAgents}/${formValues.wantAdId}`, {
                              state: { isFromConfirmationPage: true },
                            })
                            : navigate(pageURLs.agentCreatewantad, {
                              state: { isFromConfirmationPage: true },
                            })
                        }
                      >
                        Edit
                      </button>
                    </div>

                    <div className="confirm-map-wrap">
                      <OverlayTrigger
                        placement="bottom-start"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip}
                      >
                        <div className="property-tile w-auto">
                          {propertyType === IS_LAND ||
                            propertyType === IS_MULTI_FAMILY ? (
                            <img
                              src={getPropertyIcon(propertyType)}
                              alt={getPropertyIcon(propertyType)}
                              className="property-icon-img"
                            />
                          ) : (
                            propertySubTypes &&
                            propertySubTypes.length > 0 &&
                            propertySubTypes.map((subtype) => {
                              return (
                                <img
                                  src={getPropertyIcon(subtype)}
                                  alt={getPropertyIcon(subtype)}
                                  className="property-icon-img"
                                />
                              );
                            })
                          )}
                        </div>
                      </OverlayTrigger>
                      {lat && lng ? (
                        <MapboxComponent
                          center={[lng, lat]}
                          radiusMiles={drawnPolygon ? null : mapRadius}
                          showPolygon={!!drawnPolygon}
                          polygonCoordinates={drawnPolygon ? [[drawnPolygon]] : []}
                        />
                      ) : (
                        <img
                          src={ConfirmMap}
                          alt=""
                          className="disp-map-img-fixed maxh-300"
                        />
                      )}
                    </div>

                    {loadAdMustHaves()}
                    {!isItemNotExist ? (
                      <div className="nicetohaves-wrap">
                        <h2>Nice-to-haves</h2>
                        <div className="nicetohaves-inner-wrap">
                          {finalNiceToHaveList()}
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                    {_.get(formValues, "adDesc", "") ? (
                      <div className="additional-details-wrap">
                        <h2>Description</h2>
                        <p className="long-txt-fix">
                          {_.get(formValues, "adDesc", "")}
                        </p>
                      </div>
                    ) : (
                      ""
                    )}
                    {videoUrl && (
                      <div className="video-wrap">
                        <video
                          id="videoPlayer"
                          controls
                          width={isVertical ? "300px" : "100%"} // Width 300px for vertical, auto for horizontal
                          height={isVertical ? "500px" : "auto"} // Auto height for vertical, 500px for horizontal
                          className="video-player"
                        >
                          <source src={videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}

                    <div
                      className={
                        (adType == BUY ||
                          _.get(formValues, "prprtyType", "") == IS_LAND) ? `buyer-info ${getRequestedConcessions(_.get(formValues, "otherConcessions", "")) ? "nicetohaves-wrap" : ""}` : "buyer-info"
                      }
                    >
                      <h2>Additional details</h2>
                      <ul>
                        {loadTimeLine()}
                        {loadPreferredLocation()}
                        {loadStrengthenDetails()}
                      </ul>
                    </div>

                    {(adType == BUY ||
                      _.get(formValues, "prprtyType", "") == IS_LAND) &&
                      getRequestedConcessions(
                        _.get(formValues, "otherConcessions", ""),
                      ) ? (
                      <div className="buyer-info">
                        <h2>Requested Concessions From Seller</h2>
                        <ul>
                          <li>
                            {getRequestedConcessions(
                              _.get(formValues, "otherConcessions", ""),
                            )}
                          </li>
                        </ul>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};
export default AgentWantAdConfirmation;
